import { useQueries } from "@tanstack/react-query";

import { AGENCY_INCLUDED_SEATS } from "features/subscriptions/constants";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { get } from "common/helpers/HTTP";
import {
  getActiveOutreachWorkspaceSeatCounts,
  getTotalActiveOutreachSeats,
  hasActiveOutreachSubscription,
  mergeWorkspacesWithDetails,
} from "common/helpers/workspaceLicenseCounts";
import type { DetailedWorkspace, Workspace } from "common/types";

async function getWorkspaceDetailsRequest(
  workspaceId: string,
): Promise<DetailedWorkspace> {
  return (await get(`workspaces/${workspaceId}`)).workspace;
}

export function getAgencyPurchaseMinSeats(
  workspaces: Workspace[] | undefined,
): number {
  return Math.max(
    AGENCY_INCLUDED_SEATS,
    getTotalActiveOutreachSeats(workspaces),
  );
}

export default function useAgencyPurchaseMinSeats(enabled: boolean) {
  const { workspaces } = useWorkspaces();

  const outreachWorkspaceIds =
    enabled && workspaces
      ? workspaces.filter(hasActiveOutreachSubscription).map(({ id }) => id)
      : [];

  const detailQueries = useQueries({
    queries: outreachWorkspaceIds.map((workspaceId) => ({
      queryKey: CUSTOMER_QUERY_KEYS.workspaces.details(workspaceId),
      queryFn: () => getWorkspaceDetailsRequest(workspaceId),
      enabled,
    })),
  });

  let workspacesForAggregation = workspaces;
  if (enabled && workspaces) {
    const detailsById = new Map<string, DetailedWorkspace>();
    outreachWorkspaceIds.forEach((workspaceId, index) => {
      const details = detailQueries[index]?.data;
      if (details) {
        detailsById.set(workspaceId, details);
      }
    });
    workspacesForAggregation = mergeWorkspacesWithDetails(
      workspaces,
      detailsById,
    );
  }

  const totalActiveOutreachSeats = getTotalActiveOutreachSeats(
    workspacesForAggregation,
  );
  const outreachSeatBreakdown = getActiveOutreachWorkspaceSeatCounts(
    workspacesForAggregation,
  );

  return {
    minSeats: enabled
      ? getAgencyPurchaseMinSeats(workspacesForAggregation)
      : AGENCY_INCLUDED_SEATS,
    totalActiveOutreachSeats,
    outreachSeatBreakdown,
  };
}
